/** A box in window coordinates. */
export type SpotlightRect = { x: number; y: number; width: number; height: number };

export type SpotlightSize = { width: number; height: number };

/** Room the highlight leaves around its target, per axis and on each side. */
export type SpotlightPadding = { x: number; y: number };

export type SpotlightEdges = { top: number; bottom: number };

export type SpotlightPlacement = 'below' | 'above';

/** Space between the highlight and its card, enough for the card's caret. */
export const SPOTLIGHT_CARD_GAP = 12;

/** The widest a card grows, its `max-w-md`. */
export const SPOTLIGHT_CARD_MAX_WIDTH = 448;

/** Grows a target's box by its padding on every side, so the target itself does not move. */
export function padRect(rect: SpotlightRect, padding: SpotlightPadding): SpotlightRect {
  return {
    x: rect.x - padding.x,
    y: rect.y - padding.y,
    width: rect.width + 2 * padding.x,
    height: rect.height + 2 * padding.y,
  };
}

/** How wide the card is: the window less a gutter each side, capped at the card's own maximum. */
export function getCardWidth(windowWidth: number, gutter: number): number {
  return Math.max(0, Math.min(windowWidth - 2 * gutter, SPOTLIGHT_CARD_MAX_WIDTH));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Where the card goes beside its highlight, from the card's measured size.
 *
 * Below is the default, since that is where the eye is already heading. Above
 * when below would run past the safe area and above fits. When neither fits,
 * as for a target that fills most of the screen, the card takes the roomier
 * side and is clamped inside the safe area, so it may overlap the highlight
 * rather than leave the screen.
 *
 * Horizontally it centers on the target, kept inside the gutter.
 */
export function getCardPosition({
  target,
  card,
  window,
  safeArea,
  gutter,
}: {
  target: SpotlightRect;
  card: SpotlightSize;
  window: SpotlightSize;
  /** The insets the card must stay clear of, such as the status bar and the home indicator. */
  safeArea: SpotlightEdges;
  gutter: number;
}): { top: number; left: number; placement: SpotlightPlacement } {
  const minTop = safeArea.top + gutter;

  const maxBottom = window.height - safeArea.bottom - gutter;

  const belowTop = target.y + target.height + SPOTLIGHT_CARD_GAP;

  const aboveTop = target.y - SPOTLIGHT_CARD_GAP - card.height;

  const fitsBelow = belowTop + card.height <= maxBottom;

  const fitsAbove = aboveTop >= minTop;

  let placement: SpotlightPlacement = 'below';

  if (!fitsBelow) {
    if (fitsAbove) {
      placement = 'above';
    } else {
      placement = target.y - minTop > maxBottom - (target.y + target.height) ? 'above' : 'below';
    }
  }

  const top = clamp(placement === 'below' ? belowTop : aboveTop, minTop, maxBottom - card.height);

  const left = clamp(
    target.x + target.width / 2 - card.width / 2,
    gutter,
    window.width - gutter - card.width
  );

  return { top, left, placement };
}

/**
 * The scrim's outline: the whole window, with the highlight cut out as a
 * rounded rectangle. Drawn with the even-odd rule, so the inner shape is a hole.
 */
export function getCutoutPath(window: SpotlightSize, hole: SpotlightRect, radius: number): string {
  const outer = `M0,0H${window.width}V${window.height}H0Z`;

  const corner = Math.max(0, Math.min(radius, hole.width / 2, hole.height / 2));

  const { x, y, width, height } = hole;

  const right = x + width;

  const bottom = y + height;

  const inner = [
    `M${x + corner},${y}`,
    `H${right - corner}`,
    `A${corner},${corner} 0 0 1 ${right},${y + corner}`,
    `V${bottom - corner}`,
    `A${corner},${corner} 0 0 1 ${right - corner},${bottom}`,
    `H${x + corner}`,
    `A${corner},${corner} 0 0 1 ${x},${bottom - corner}`,
    `V${y + corner}`,
    `A${corner},${corner} 0 0 1 ${x + corner},${y}`,
    'Z',
  ].join('');

  return `${outer}${inner}`;
}

/**
 * The four bands of scrim around the highlight. Each takes touches, so a tap
 * outside the highlight never reaches the screen beneath, while the highlight
 * itself is left uncovered and stays usable.
 *
 * Always all four, named, a band with no room being zero-sized, so a moving
 * highlight resizes the same four views rather than mounting new ones. Inside
 * iOS's full-window overlay a newly mounted band landed above the card and
 * swallowed its buttons.
 */
export function getScrimBands(
  window: SpotlightSize,
  hole: SpotlightRect
): Record<'top' | 'bottom' | 'left' | 'right', SpotlightRect> {
  const top = Math.max(0, hole.y);

  const bottom = Math.min(window.height, hole.y + hole.height);

  const left = Math.max(0, hole.x);

  const right = Math.min(window.width, hole.x + hole.width);

  return {
    top: { x: 0, y: 0, width: window.width, height: top },
    bottom: { x: 0, y: bottom, width: window.width, height: Math.max(0, window.height - bottom) },
    left: { x: 0, y: top, width: left, height: Math.max(0, bottom - top) },
    right: {
      x: right,
      y: top,
      width: Math.max(0, window.width - right),
      height: Math.max(0, bottom - top),
    },
  };
}
